import { immutableDate, immutableList, immutableMap, primitive, serializable } from '../serialize/serialize';
import { createImmutableList, createImmutableMap, IImmutableList, IImmutableMap, ImmutableDate } from '../state/redux';
import { FormFieldV2, ISAFActionExecutionResponse, SAFActionProvider } from './safDiagnostic.models';

/**
 * Specifies if a node in the json is a base textNode,
 * or one of the extended choiceNode,
 * dafDiagnosticNode,
 * or safActionNode types, so it can be rendered correctly.
 */
export enum NodeType {
  TextNode = 'textnode',
  ChoiceNode = 'choicenode',
  DafDiagnosticNode = 'dafdiagnosticnode',
  SafDiagnosticNode = 'safdiagnosticnode',
  InputNode = 'inputnode',
  RedirectNode = 'redirectnode',
  PartnerSolutionNode = 'partnersolutionnode'
}

/**
 * Required.
 * This is an enumeration value that indicates whether the content field contains
 * a full embedded HTML string or references a content source (e.g. via URL or GUID).
 */
export enum ContentType {
  Embedded = 'embedded',
  EmbeddedHtml = 'embeddedhtml',
  EmbeddedPlainText = 'embeddedplaintext',
  AlchemyResource = 'alchemyresource',
  C3 = 'c3',
  Dmc = 'dmc',
  Soc = 'soc',
  ServerDiagnostic = 'ServerDiagnostic',
  Branching = 'Branching',
  ServiceNow= 'servicenow',
  PPAC = 'ppac'
}

/**
 * Whenever the renderer renders a node,
 * it generates an event signal to the host UI,
 * reporting the node ID and other values for telemetry purposes
 */
export enum OutcomeSignal {
  Successful = 'successful',
  Unsuccessful = 'unsuccessful',
  Indeterminate = 'indeterminate'
}

/**
 * Daf diagnostic types
 */
export enum ActionType {
  DafV1 = 'dafv1',
  DafV1WithParams = 'dafv1WithParams',
  DafV2 = 'dafv2',
  DafV2WithParams = 'dafv2WithParams',
  SkylightSAF = 'skylightSAF'
}

export class SolutionContent {
  @serializable(primitive())
  public SolutionTitle!: string;

  @serializable(primitive())
  public SolutionContent!: string;
}

/**
 * Text field class with content type and content.
 * Used for providing any form of text to be renderd in component
 */
export class TextField {
  @serializable(primitive())
  public contentType!: ContentType;

  @serializable(primitive())
  public content!: string;

  @serializable(primitive())
  public contentText!: string;

  @serializable(primitive())
  public contentTitle!: string;

  @serializable(primitive())
  public dynamic!: boolean;
}

/**
 * Text node type (example: leaf node that shows alchemy insights)
 */
export class TextNode {
  @serializable(primitive())
  public type!: NodeType;

  @serializable(primitive())
  public id!: string;

  @serializable(primitive())
  public name!: string;

  @serializable(primitive())
  public tags!: string;

  @serializable(immutableMap(TextField))
  public title!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public text!: IImmutableMap<TextField>;

  @serializable(primitive())
  public outcomeSignal!: OutcomeSignal;

  @serializable(primitive())
  public dialogId!: string;
}

/**
 * Dynamic choice variables - a set of variable names and values 
 * to be set to the global store once the user clicks on a dynamic choice
 * targetValue - if true the value set here will be used to pick the targetNodeId 
 * by evaulating the conditions inside the dynamicChoiceTargets array
 * eg: {SelectedOutput} === 'M365 office', SelectedOutput will be 
 * replaced by the target value and the logic evaluated
 */
 export class DynamicVariableValue{
  @serializable(primitive())
  public value: string;
  @serializable(primitive())
  public variableToSet: string;
  @serializable(primitive())
  public targetValue: boolean;
}

/**
 * Dynamic choice values - a set of variable names and values 
 * to be set to the global store once the user clicks on a dynamic choice
 */
 export class DynamicChoiceValue{
  @serializable(primitive())
  public value: any;
  @serializable(primitive())
  public variableToSet: string;
}

/**
 * Dynamic choice variable list -added to have dynamic targetNodeId after SAF execution
 */
export class DynamicChoiceVariable{
  @serializable(primitive())
  public text: string;
  @serializable(immutableList(immutableMap(DynamicVariableValue)))
  public values: IImmutableList<IImmutableMap<DynamicVariableValue>>;
}

/**
 * Static Choice element to be renderd for ChoiceNode type
 * This holds the target node id that will be used for fetching next node
 */
export class Choice {
  @serializable(primitive())
  public id!: string;

  @serializable(immutableMap(TextField))
  public text!: IImmutableMap<TextField>;

  @serializable(primitive())
  public raiseEventName!: string;

  @serializable(primitive())
  public targetNodeId!: string;

  @serializable(primitive())
  public failedTargetNodeId!: string;

  @serializable(primitive())
  public eventHold!: boolean;

  @serializable(immutableList(immutableMap(DynamicChoiceValue)))
  public values: IImmutableList<IImmutableMap<DynamicChoiceValue>>;
}

/**
 * Specifies a target (for moving to the next node) that result from executing the diagnostic action,
 * if execution succeeded (the action ran and exited with some kind of return value(s)).
 */
 export class Target {
  @serializable(primitive())
  public condition!: string;

  @serializable(primitive())
  public targetNodeId!: string;
}

/**
 * Choice node that has a list of static choices
 */
export class ChoiceNode extends TextNode {
  @serializable(primitive())
  public dynamicChoices!: boolean;

  @serializable(primitive())
  public dynamicChoiceTargetNodeId!: string;

  @serializable(immutableList(immutableMap(Choice)))
  public staticChoices!: IImmutableList<IImmutableMap<Choice>>;

  @serializable(immutableMap(TextField))
  public question!: IImmutableMap<TextField>;

  @serializable(immutableList(immutableMap(Target)))
  public dynamicChoiceTargets: IImmutableList<IImmutableMap<Target>> = createImmutableList([]);

  @serializable(immutableMap(DynamicChoiceVariable))
  public dynamicChoiceVariables!: IImmutableMap<DynamicChoiceVariable>;

  @serializable(primitive())
  public rootObjectPath: string
}

/**
 * Collection of constraints to be used for input node
 */
export class InputConstraints {
  @serializable(primitive())
  public required!: boolean;

  @serializable(primitive())
  public maxLength!: number;

  @serializable(primitive())
  public minLength!: number;

  @serializable(primitive())
  public pattern!: string;
}

/**
 * Input node 
 */
 export class InputNode extends TextNode {
  @serializable(primitive())
  public isMultiLine!: boolean;

  @serializable(primitive())
  public default!: string|number;

  @serializable(primitive())
  public variableToSet!: string;

  @serializable(immutableMap(TextField))
  public tooltip!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public buttonText!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public placeholder!: IImmutableMap<TextField>;

  @serializable(immutableMap(InputConstraints))
  public constraints!: IImmutableMap<InputConstraints>;
  
  @serializable(primitive())
  public defaultTargetNodeId!: string;

  @serializable(immutableList(immutableMap(Target)))
  public targets!: IImmutableList<IImmutableMap<Target>>;
}

export class RedirectNode {
  @serializable(primitive())
  public type!: NodeType;
  
  @serializable(primitive())
  public id!: string;

  @serializable(primitive())
  public targetDialogId!: string;

  @serializable(primitive())
  public failedTargetNodeId!: string;
}

/**
 * Variable providers
 */
 export enum IVariableProvider{
  GlobalVars = "GlobalVars",
  ChoiceText = "ChoiceText",
  ChoiceId = "ChoiceId",
  Response = "Response",
  HardCoded = "HardCoded"
}

/**
 * ReadWriteAction
 */
export enum ReadWriteAction{
  ReadContextVariables = "ReadContextVariables",
  ReadGlobalVariables = "ReadGlobalVariables",
  WriteGlobalVariables = "WriteGlobalVariables"
}

export class AdhocVariableToSet {
  @serializable(primitive())
  public key!: string;

  @serializable(primitive())
  public path!: string;

  @serializable(primitive())
  public provider!: IVariableProvider;

  @serializable(primitive())
  public value!: any;

  @serializable(primitive())
  public default!: any;
}

export class ContextVariableToSet {
  @serializable(primitive())
  public context!: string;

  @serializable(immutableList(immutableMap(AdhocVariableToSet)))
  public variables: IImmutableList<IImmutableMap<AdhocVariableToSet>>;
}


/**
 * If provided, this specifies parameters to pass to the diagnostic action,
 * defined as expressions that can include the current values of global variables.
 * Each value is an expression that can include variables (in curly braces, not case-sensitive).
 * For example, if there is a variable named numberOfCameras that needs
 * to be passed to a diagnostic action as a numCameras parameter,
 * parameterName would be numCameras and parameterValue would be {numberOfCameras}.
 *
 * When used with onDafDiagnosticsActionCompleted:
 * This ParamOrVar will be used for navigation based on the conditon
 * to evaluate and the other purpose is to provide dynamic choices to render
 */
export class ParamOrVar {
  @serializable(primitive())
  public name!: string;

  /**  can be string or JSON string.
   * If the ParamVar name matches dynamicChoicesVariableName inside the choiceNode,
   we parse the value and map it to new choices that will have the same dynamicChoiceTargetNodeId possibly differing by varibales
   value = ['Front Camera', 'Back Camera' .. etc] as JSON
   * */
  @serializable(primitive())
  public value!: string;
}

/**
 * if condition is true DAF will be executed and if false text will be shown
 */
export class Precondition {
  @serializable(primitive())
  public condition!: string;
  @serializable(immutableMap(TextField))
  public text!: IImmutableMap<TextField>;
  @serializable(primitive())
  public targetNodeId!: string;
}

/**
 * Daf Diagnostic model
 */
export class DAFDiagnosticNode extends TextNode {
  @serializable(primitive())
  public actionType!: ActionType;

  @serializable(primitive())
  public actionReference!: string;

  @serializable(primitive())
  public timeoutSeconds!: number;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public parameters!: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public variables!: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableList(immutableMap(Target)))
  public targets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(immutableList(immutableMap(Target)))
  public failureTargets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(immutableList(immutableMap(Target)))
  public defaultTargets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(primitive())
  public defaultTargetNodeId!: string;

  @serializable(primitive())
  public failedTargetNodeId!: string;

  @serializable(primitive())
  public dynamicChoice!: string;

  @serializable(primitive())
  public dynamicChoicePosition!: number;

  @serializable(immutableMap(Precondition))
  public preCondition!: IImmutableMap<Precondition>;
}

/**
 * Partner Solution model
 * the text field will hold the solution Id and the solution type for the partner solution
 * successTargetNodeId will be used to render the next node if host returns true after rendering partner solution
 * failedTargetNodeId will be used to render the next node if host returns false after rendering partner solution
 */
export class PartnerSolutionNode extends TextNode {
  @serializable(primitive())
  public failedTargetNodeId!: string;
  @serializable(primitive())
  public successTargetNodeId!: string;
}

/**
 * Saf Diagnostic model
 * the payload will hold the the DiagnosticInitResponse (saf diagnostic payload)
 * successTargetNodeId will be used to render the next node if the diagnostic component render was successful
 * failedTargetNodeId will be used to render the next node if the diagnostic component render was unsuccessful
 * diagnosticUId unique diagnostic id for tracking multiple diagnostics in UI
 */
export class SAFDiagnosticNode extends TextNode {
  @serializable(primitive())
  public payload!: string;
  @serializable(primitive())
  public failedTargetNodeId!: string;
  @serializable(primitive())
  public successTargetNodeId!: string;
  // added until we can handle multiple diagnostics in redux
  @serializable(primitive())
  public diagnosticUId!: string;
}

/**
 * Contains metadata about the json document.
 */
export class Meta {
  @serializable(primitive())
  public id!: string;

  @serializable(primitive())
  public parentDialogId!: string;

  @serializable(primitive())
  public dialogIdChain!: string;

  @serializable(primitive())
  public name!: string;

  @serializable(primitive())
  public parentDialogName!: string;
  
  @serializable(primitive())
  public title!: string;

  @serializable(primitive())
  public description!: string;

  @serializable(primitive())
  public keywords!: string;

  @serializable(primitive())
  public team!: string;

  @serializable(immutableDate())
  public lastModifiedAt!: ImmutableDate;

  @serializable(primitive())
  public lastModifiedBy!: string;

  @serializable(primitive())
  public version!: number;

  @serializable(primitive())
  public locale!: string;

  @serializable(primitive())
  public supportAreaReference!: string;
}

/**
 * Model to hold the result coming back from a daf diagnostics
 * IsSuccess: indicating whether the current execution was  successful or not
 * Results: ParamOrVar list holding conditions for rendering next target node
 * DynamicChoices: list of labels for the dynamic choice to be shown with the static choices of the taget node to display
 */
export class DiagnosticsActionResult {
  @serializable(primitive())
  public IsSuccess: boolean;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public Results: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableList(primitive()))
  public DynamicChoices: IImmutableList<string>;
}

export enum DiagnosticsActionStatus {
  InProgress,
  CompletedSuccessfully,
  Error,
  FetchingDiagnosticsInProgress,
  FetchingDiagnosticsCompleted
}

/**
 * GraphNode model created for UI purposes and for serialization
 * expanded property is added for
 */
export class GraphNode extends TextNode {
  @serializable(primitive())
  public actionType!: ActionType;

  @serializable(primitive())
  public uId!: string;

  @serializable(primitive())
  public actionReference!: string;

  @serializable(primitive())
  public timeoutSeconds!: number;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public parameters!: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public variables!: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableList(immutableMap(Target)))
  public targets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(immutableList(immutableMap(Target)))
  public failureTargets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(immutableList(immutableMap(Target)))
  public defaultTargets!: IImmutableList<IImmutableMap<Target>>;

  @serializable(primitive())
  public defaultTargetNodeId!: string;

  @serializable(primitive())
  public failedTargetNodeId!: string;
  
  @serializable(primitive())
  public successTargetNodeId!: string;

  @serializable(primitive())
  public payload!: string;

  @serializable(primitive())
  public dynamicChoicesVariableName!: string;

  @serializable(primitive())
  public dynamicChoiceTargetNodeId!: string;

  @serializable(immutableMap(DynamicChoiceVariable))
  public dynamicChoiceVariables!: IImmutableMap<DynamicChoiceVariable>;

  @serializable(immutableList(immutableMap(Choice)))
  public staticChoices!: IImmutableList<IImmutableMap<Choice>>;

  @serializable(immutableList(immutableMap(Choice)))
  public dynamicChoicesList: IImmutableList<IImmutableMap<Choice>> = createImmutableList([]);

  @serializable(immutableList(immutableMap(Target)))
  public dynamicChoiceTargets: IImmutableList<IImmutableMap<Target>> = createImmutableList([]);

  @serializable(primitive())
  public rootObjectPath: string

  @serializable(immutableMap(TextField))
  public question!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public title!: IImmutableMap<TextField>;

  @serializable(primitive())
  public type!: NodeType;

  @serializable(primitive())
  public id!: string;

  @serializable(primitive())
  public parentUId!: string;

  @serializable(primitive())
  public name!: string;

  @serializable(primitive())
  public tags!: string;

  @serializable(immutableMap(TextField))
  public text!: IImmutableMap<TextField>;

  @serializable(primitive())
  public outcomeSignal!: OutcomeSignal;

  @serializable(primitive())
  public dynamicChoices!: boolean;

  @serializable(immutableMap(Choice))
  public selectedChoice!: IImmutableMap<Choice>;

  @serializable(immutableMap(DiagnosticsActionResult))
  public diagnosticsResult = createImmutableMap(new DiagnosticsActionResult());

  @serializable(primitive())
  public diagnosticsActionStatus!: DiagnosticsActionStatus;

  @serializable(primitive())
  public timerId!: number;

  @serializable(primitive())
  public isInEditMode = true;

  @serializable(primitive())
  public isFirstRender = true;

  @serializable(primitive())
  public isMultiLine!: boolean;

  @serializable(primitive())
  public default!: string|number;

  @serializable(immutableMap(TextField))
  public tooltip!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public placeholder!: IImmutableMap<TextField>;

  @serializable(immutableMap(TextField))
  public buttonText!: IImmutableMap<TextField>;

  @serializable(immutableMap(InputConstraints))
  public constraints!: IImmutableMap<InputConstraints>;

  @serializable(immutableMap(FormFieldV2))
  public InputFormField!: IImmutableMap<FormFieldV2>;

  @serializable(primitive())
  public variableToSet!: string;

  // added until we can handle multiple diagnostics in redux
  @serializable(primitive())
  public diagnosticUId!: string;

  @serializable(immutableMap(Precondition))
  public preCondition!: IImmutableMap<Precondition>;

  @serializable(primitive())
  public skipNode!: boolean;

  @serializable(primitive())
  public targetDialogId!: string;

  @serializable(immutableList(immutableMap(ContextVariableToSet)))
  public adhocVariables: IImmutableList<IImmutableMap<ContextVariableToSet>>;
}

/**
 * Root class
 */
export class JsonDocument {
  @serializable(primitive())
  public schemaVersion!: number;

  @serializable(immutableMap(Meta))
  public meta!: IImmutableMap<Meta>;

  @serializable(immutableList(immutableMap(ParamOrVar)))
  public initialVariables!: IImmutableList<IImmutableMap<ParamOrVar>>;

  @serializable(immutableMap(TextField))
  public topHeadingText!: IImmutableMap<TextField>;

  @serializable(primitive())
  public startNodeId!: string;

  @serializable(immutableList(immutableMap(GraphNode)))
  public nodes!: IImmutableList<IImmutableMap<GraphNode>>;
}

export class SafDiagnosticsSettings{
  @serializable(primitive())
  public multilineTextFieldRowCount?: number;

  @serializable(primitive())
  public titleStringLimit?: number;
 
  @serializable(primitive())
  public buttonLabelLimit?: number;
 
  @serializable(primitive())
  public safActionMaxRetries?: number;

  @serializable(primitive())
  public timeoutInSeconds?: number;

  @serializable(primitive())
  public serviceTimerDelayInMilliseconds?: number;

  @serializable(primitive())
  public hideProgressStrings?: boolean;

  @serializable(primitive())
  public hideProgressBar?: boolean;
}

export class StaticConfig {

  @serializable(primitive())
  public correlationId?: string;

  @serializable(primitive())
  public sessionId?: string;

  @serializable(primitive())
  public hostAppName?: string;

  @serializable(primitive())
  public prefix?: string;

  @serializable(primitive())
  public locale?: string;

  @serializable(primitive())
  public timeoutInSeconds?: number;

  @serializable(primitive())
  public enableScrollToBottom!: boolean;

  @serializable(primitive())
  public enableScrollToTop!: boolean;

  @serializable(primitive())
  public logLinkClicks!: boolean;

  @serializable(primitive())
  public useCallBacksForSAFApis!: boolean;

  @serializable(primitive())
  public disableValidationException!: boolean;

  @serializable(primitive())
  public scrollRef!: string;

  @serializable(immutableMap(SafDiagnosticsSettings))
  safDiagnosticsSettings?: IImmutableMap<SafDiagnosticsSettings>;

  @serializable(primitive())
  public debugRootStore!: boolean; 

  @serializable(immutableList(immutableMap(JsonDocument)))
  public jsonDocuments?: IImmutableList<IImmutableMap<JsonDocument>>;
}

export enum RaisedEventStatus{
  inProgress = 'inprogress',
  success = 'success',
  failure = 'failure'
}

export class RaisedEventState {
  @serializable(primitive())
  public raisedBy?: string;

  @serializable(primitive())
  public eventName?: string;

  @serializable(primitive())
  public status?: RaisedEventStatus;
}

export enum ExternalApi{
  ContentRequestHandler = 'ContentRequestHandler',
  GetSAFActionSchema = 'GetSAFActionSchema',
  ExecuteSAFAction = 'ExecuteSAFAction'
}

export enum ComponentOverrides{
  DiagnosticProgressBar = 'DiagnosticProgressBar',
  PartnerSolution = 'PartnerSolution',
}

export interface IPartnerComponentNodeProps {
  solutionType: string;
  solutionId: string;
  nodeId: string;
  uId: string;
  dialogId: string;
}

export class APIRetryBase{
  @serializable(primitive())
  public name?: ExternalApi;

  @serializable(primitive())
  public maxRetriesCount?: number = 3;

  @serializable(primitive())
  public enabled?: boolean;
}

export class APIRetry extends APIRetryBase{
  @serializable(primitive())
  public retriesCount?: number = 0;
}

export class AlchemyResource{
  @serializable(primitive())
  public contentId?: string;

  @serializable(primitive())
  public contentType?: ContentType;

  @serializable(primitive())
  public solutionContent?: string;

  @serializable(primitive())
  public solutionTitle?: string;

  @serializable(primitive())
  public isLoading?: boolean;

  @serializable(primitive())
  public isDynamic?: boolean;
}

export class ReadWriteParams{
  /** Required property for providing action type to be executed */
  public actionType: ReadWriteAction;
  /** Required property if actionType ==  ReadContextVariables, property to provide the context key, example ticketData/raveData*/
  public context: string;
  /** Required property if actionType ==  WriteGlobalVariables, property to provide an array of paramOrVar to be written to globalVars **/
  public parameters: ParamOrVar[];
  /**optional property to set limit on context key/value length */
  public contextKeyValueLimit?: number;
}

export interface IUpdateAdhocVariablesParams{
  adhocVariables: IImmutableList<IImmutableMap<ContextVariableToSet>>,
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  selectedChoice?: IImmutableMap<Choice>,
  safResponse?: ISAFActionExecutionResponse,
  actionProvider?: SAFActionProvider,
  dafResponse?: DiagnosticsActionResult
}

export interface IContextMetadata {
  key: string;
  value: any;
}

export interface ITextFieldOwnProps extends React.HTMLAttributes<HTMLElement> {
  textContent: IImmutableMap<TextField>;
  className?: string;
  titleClassName?: string;
  sessionId?: string;
  node?: IImmutableMap<GraphNode>;
  meta?: IImmutableMap<Meta>;
  logLinkClicks?: boolean;
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>;
}

export interface ContentIdResource{
  contentId?: string;
  isDynamic?: boolean;
  contentType?: ContentType
}